import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// ✅ Register once at module level
gsap.registerPlugin(ScrollTrigger);

// ✅ Stable reference outside component
const reasons = [
  {
    id: '01',
    title: '24/7 Support',
    description:
      'Dedicated support across TikTok Shop, Shopify, and eBay UK — every step of the way, whenever you need us.',
  },
  {
    id: '02',
    title: 'Expert Team',
    description:
      'Deep platform expertise and UK tax compliance knowledge, backed by a proven track record of scaling British brands.',
  },
  {
    id: '03',
    title: 'UK Marketplace Specialists',
    description:
      'We are a UK-based ecommerce agency specialising in TikTok Shop, Shopify, and eBay UK — handling every detail from setup to full-scale brand launch.',
  },
  {
    id: '04',
    title: 'Revenue-Focused Builds',
    description:
      "We don't just build stores — we build revenue engines tailored to the way UK consumers shop online.",
  },
  {
    id: '05',
    title: 'Three-Step Framework',
    description:
      'Whether dropshipping, white label, or private label, our three-step framework gets you live fast and optimised for long-term success across all major UK marketplaces.',
  },
];

const WhyChooseUs = () => {
  const sectionRef = useRef(null);
  const rowRefs = useRef([]);

  useEffect(() => {
    // ✅ FIX 1: Wrap GSAP setup inside requestAnimationFrame to defer 
    // measurement calculations until after initial paint, preventing forced reflows.
    let ctx;
    const rafId = requestAnimationFrame(() => {
      ctx = gsap.context(() => {
        rowRefs.current.forEach((row) => {
          if (!row) return;

          // ✅ FIX 2: Added willChange to hint browser compositor ahead of scroll
          gsap.fromTo(
            row,
            { opacity: 0, y: 40 },
            {
              opacity: 1,
              y: 0,
              duration: 0.8,
              ease: 'power3.out',
              scrollTrigger: {
                trigger: row,
                start: 'top 88%',
                toggleActions: 'play none none reverse',
                fastScrollEnd: true, // Optimizes rapid mobile scrolling
              },
            }
          );
        });
      }, sectionRef);
    });

    return () => {
      cancelAnimationFrame(rafId);
      if (ctx) ctx.revert();
    };
  }, []);

  return (
    <section ref={sectionRef} style={styles.outerSectionCanvas} className="wcu-section">
      <style>{`
        .wcu-row { 
          position: relative; 
          overflow: hidden; 
          will-change: transform, opacity; /* ✅ Pre-informs browser for smooth composite layers */
        }
        .wcu-row-inner { transition: background 0.3s ease; }
        .wcu-row:hover .wcu-row-inner { background: #F97316; }
        .wcu-row-stage { position: relative; width: 100%; }
        .wcu-row-title,
        .wcu-row-desc {
          position: absolute;
          top: 50%; left: 50%;
          width: 100%;
          transform: translate(-50%, -50%);
          transition: opacity 0.35s ease, transform 0.35s ease, color 0.3s ease;
        }
        .wcu-row-title { opacity: 1; }
        .wcu-row:hover .wcu-row-title { opacity: 0; transform: translate(-50%, -65%); color: #FFFFFF; }
        .wcu-row-desc { opacity: 0; transform: translate(-50%, -35%); pointer-events: none; color: #0A0A0A; }
        .wcu-row:hover .wcu-row-desc { opacity: 1; transform: translate(-50%, -50%); color: #FFFFFF; }
        .wcu-row-index { transition: color 0.3s ease, opacity 0.3s ease; }
        .wcu-row:hover .wcu-row-index { color: #FFFFFF; opacity: 0.8; }
        @media (max-width: 768px) {
          .wcu-row-title { font-size: clamp(24px, 8vw, 34px) !important; }
          .wcu-row-desc { font-size: clamp(13px, 4vw, 16px) !important; padding: 0 4vw !important; }
          .wcu-row-stage { min-height: 150px !important; }
          .wcu-row-inner { padding: 24px 5vw !important; }
          .wcu-banner { height: 32vh !important; }
          .wcu-title { font-size: clamp(28px, 8vw, 38px) !important; }
        }
      `}</style>

      {/* HEADER */}
      <div style={styles.headerBlock}>
        <span style={styles.eyebrowLabel}>Why Choose Us</span>
        <h2 style={styles.brandTitleHeader} className="wcu-title">
          Built for the <em style={styles.highlightWord}> Market</em>
        </h2>
        <p style={styles.headerSubtext}>
          From account setup to full-scale brand launch — here&apos;s what makes us different.
        </p>
      </div>

      {/* Video Banner */}
      <div style={styles.topImageBannerCanvas} className="wcu-banner">
        {/*
          ✅ FIX 3: 
          - Added width/height attributes for explicit layout space reservation.
          - Added aria-hidden="true" to decorative video to pass Lighthouse accessibility.
        */}
        <video
          src="https://ik.imagekit.io/g9mnvfpan/Softbeck%20Limited/5585988-hd_1080_1920_30fps%20(1).mp4"
          autoPlay
          loop
          muted
          playsInline
          preload="none"
          aria-hidden="true"
          width="1400"
          height="600"
          style={styles.bannerImageSource}
        />
        <div style={styles.bannerGradientDimmer} />
      </div>

      {/* ROWS */}
      <div style={styles.rowsWrapper}>
        {reasons.map((reason, index) => (
          <div
            key={reason.id}
            className="wcu-row"
            ref={(el) => (rowRefs.current[index] = el)}
            style={styles.row}
          >
            <div className="wcu-row-inner" style={styles.rowInner}>
              <span className="wcu-row-index" style={styles.rowIndex}>{reason.id}</span>
              <div className="wcu-row-stage" style={styles.rowStage}>
                <h3 className="wcu-row-title" style={styles.rowTitle}>{reason.title}</h3>
                <p className="wcu-row-desc" style={styles.rowDesc}>{reason.description}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* SERVICE CHIPS */}
      <div style={styles.serviceRow}>
        <div style={{ ...styles.serviceChip, ...styles.chipTiktok }}>
          <span style={{ ...styles.chipDot, backgroundColor: '#ff0050' }} />
          TikTok Shop UK
        </div>
        <div style={{ ...styles.serviceChip, ...styles.chipShopify }}>
          <span style={{ ...styles.chipDot, backgroundColor: '#5a8a1a' }} />
          Shopify
        </div>
        <div style={{ ...styles.serviceChip, ...styles.chipEbay }}>
          <span style={{ ...styles.chipDot, backgroundColor: '#c8111a' }} />
          eBay UK
        </div>
      </div>
    </section>
  );
};

const styles = {
  outerSectionCanvas: {
    backgroundColor: '#FFFFFF',
    width: '100%',
    position: 'relative',
    boxSizing: 'border-box',
    paddingTop: '100px',
    paddingBottom: '100px',
  },
  topImageBannerCanvas: {
    width: '100%',
    maxWidth: '1400px',
    margin: '0 auto',
    height: '60vh',
    position: 'relative',
    overflow: 'hidden',
    borderRadius: '28px',
    backgroundColor: '#000000',
  },
  bannerImageSource: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center center',
  },
  bannerGradientDimmer: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(to bottom, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.05) 50%, rgba(0,0,0,0.35) 100%)',
  },
  headerBlock: {
    maxWidth: '720px',
    margin: '64px auto 0',
    padding: '0 6vw',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  eyebrowLabel: {
    fontFamily: 'epilogue',
    fontSize: '19px',
    fontWeight: '700',
    letterSpacing: '0.3em',
    textTransform: 'uppercase',
    color: '#F97316',
    marginBottom: '18px',
  },
  brandTitleHeader: {
    fontFamily: 'epilogue',
    color: '#0A0A0A',
    fontSize: 'clamp(32px, 4.6vw, 56px)',
    fontWeight: '500',
    letterSpacing: '-0.02em',
    lineHeight: '1.08',
    margin: '0 0 16px 0',
  },
  highlightWord: { color: '#F97316', fontStyle: 'normal' },
  headerSubtext: {
    fontFamily: 'epilogue',
    fontSize: 'clamp(14px, 1.2vw, 17px)',
    fontWeight: '500',
    lineHeight: '1.6',
    color: '#666666',
    margin: 0,
    maxWidth: '520px',
  },
  rowsWrapper: {
    maxWidth: '1500px',
    margin: '64px auto 0',
    padding: '0 6vw',
    borderTop: '1px solid #ECECEC',
  },
  row: { borderBottom: '1px solid #ECECEC' },
  rowInner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '20px 24px',
    cursor: 'pointer',
  },
  rowIndex: {
    fontFamily: 'epilogue',
    fontSize: '13px',
    fontWeight: '700',
    letterSpacing: '0.25em',
    color: '#CDC7BD',
    marginBottom: '14px',
    opacity: 0.7,
  },
  rowStage: {
    minHeight: 'clamp(120px, 14vw, 190px)',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowTitle: {
    fontFamily: 'epilogue',
    fontSize: 'clamp(32px, 5vw, 60px)',
    fontWeight: '500',
    letterSpacing: '-0.02em',
    lineHeight: '1.1',
    color: '#0A0A0A',
    margin: 0,
    textAlign: 'center',
  },
  rowDesc: {
    fontFamily: 'epilogue',
    fontSize: 'clamp(15px, 1.8vw, 22px)',
    fontWeight: '500',
    lineHeight: '1.5',
    letterSpacing: '-0.01em',
    color: '#0A0A0A',
    margin: 0,
    textAlign: 'center',
    padding: '0 8vw',
    maxWidth: '760px',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  serviceRow: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginTop: '56px',
  },
  serviceChip: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    borderRadius: '100px',
    fontSize: '12px',
    fontWeight: '600',
    letterSpacing: '0.04em',
    border: '1px solid rgba(229, 229, 229, 0.8)',
    background: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(4px)',
    color: '#555',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  /* ✅ FIX 4: Fixed chip indicator dot dimensions to match chip height ratio */
  chipDot: { width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0 },
  chipTiktok: { color: '#ff0050', borderColor: 'rgba(255,0,80,0.15)', background: 'rgba(255,0,80,0.04)' },
  chipShopify: { color: '#5a8a1a', borderColor: 'rgba(90,138,26,0.15)', background: 'rgba(90,138,26,0.04)' },
  chipEbay:   { color: '#c8111a', borderColor: 'rgba(200,17,26,0.15)', background: 'rgba(200,17,26,0.04)' },
};

export default WhyChooseUs;